import React, { useState } from "react";
import "./ContactForm.css";
import "../socialMedia/SocialMedia.css";
import { socialMediaLinks, greeting } from "../../portfolio";
import { useLanguage } from "../../context/LanguageContext";
import translations from "../../i18n/translations";
import MailIconPNG from "../../assets/images/mail.png";
import MailIconGIF from "../../assets/images/mail.gif";

const FORMSPREE_ENDPOINT = "https://formspree.io/f/mbdwepqy";

const SERVICES_EN = [
  "AI / ML Engineering",
  "Full Stack Development",
  "Backend API Design",
  "Cloud & DevOps",
  "Data Science & Analytics",
  "System Architecture",
  "Other",
];

const SERVICES_JA = [
  "AI / ML エンジニアリング",
  "フルスタック開発",
  "バックエンド API 設計",
  "クラウド & DevOps",
  "データサイエンス & 分析",
  "システムアーキテクチャ",
  "その他",
];

function ContactCardIcon({ type }) {
  if (type === "github") {
    return (
      <span className="social-icon github cf-card-social-icon" aria-hidden="true">
        <svg className="github-icon" width="45px" height="45px" viewBox="0 0 300 300">
          <path
            id="body"
            d="M112.5,234.4v43.1c24.5,7.5,50.5,7.5,75,0V225c-0.4-11.1-4.9-21.6-12.7-29.6c30-3.4,59.5-23.5,59.5-64.1
            c0.9-13.9-3.3-27.7-11.8-38.7c4.1-11.6,3.7-24.4-1.3-35.7c0,0-11.2-3.7-37.5,13.8c-22.1-6.1-45.3-6.1-67.4,0
            C90.6,53.3,78.9,56.8,78.9,56.8c-5,11.3-5.4,24.1-1.3,35.7c-8.5,11.1-12.7,24.8-11.9,38.7c0.2,33.8,26.4,61.8,60.2,64.1
            c-5,4.7-8.1,11.1-8.6,18"
          />
          <path
            id="arm"
            d="M125.2,213.4c-13.9,7.8-31.5,2.8-39.3-11.2c-4.3-7.7-12-12.8-20.7-13.8c-11.2,0-4.6,6.4,0,9.4
            c6,5.1,10.8,11.5,13.8,18.8c2.9,7.4,7.7,24.1,41.5,17.8"
          />
        </svg>
      </span>
    );
  }

  if (type === "linkedin") {
    return (
      <span className="social-icon linkedin cf-card-social-icon" aria-hidden="true">
        <svg
          className="linkedin-icon"
          xmlns="http://www.w3.org/2000/svg"
          width="40"
          viewBox="0 0 24 24"
          fill="#FFFFFF"
          strokeWidth="1"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
          <rect x="2" y="9" width="4" height="12" />
          <circle cx="4" cy="4" r="2" />
        </svg>
      </span>
    );
  }

  return (
    <span className="social-icon google cf-card-social-icon" aria-hidden="true">
      <img src={MailIconPNG} className="mail-img google-icon" alt="" />
      <img src={MailIconGIF} className="mail-gif google-icon" alt="" />
    </span>
  );
}

export default function ContactForm({ theme }) {
  const { lang } = useLanguage();
  const t = translations[lang].contactForm;
  const tGreeting = translations[lang].greeting;
  const services = lang === "ja" ? SERVICES_JA : SERVICES_EN;

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: services[0],
    details: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  function handleChange(e) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (status) setStatus(null);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setStatus(null);

    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone || "N/A",
          service: form.service,
          message: form.details,
          _subject: `[Portfolio] ${form.service} — ${form.name}`,
          _replyto: form.email,
        }),
      });

      if (!res.ok) throw new Error("Formspree request failed");

      setStatus("success");
      setForm({
        name: "",
        email: "",
        phone: "",
        service: services[0],
        details: "",
      });
    } catch {
      setStatus("error");
    } finally {
      setSubmitting(false);
    }
  }

  const isDark = theme.name === "dark";

  // All colours pulled from the shared theme — same tokens used elsewhere in the site
  const cardBg      = theme.projectCard;
  const inputBg     = isDark ? theme.body : "#ffffff";
  const inputBorder = isDark ? "#444444" : "#d4bfcb";
  const labelColor  = theme.secondaryText;

  return (
    <div
      className="cf-wrapper"
      data-theme={theme.name}
      style={{
        backgroundColor: theme.body,
        "--cf-accent": theme.accentBright,
        "--cf-accent-ring": isDark ? "rgba(252,16,86,0.20)" : "rgba(252,16,86,0.15)",
      }}
    >
      <div className="cf-container">

        {/* ── LEFT: Form ─────────────────────────────── */}
        <div className="cf-panel" style={{ backgroundColor: cardBg }}>
          <h2 className="cf-title" style={{ color: theme.text }}>{t.title}</h2>
          <p className="cf-subtitle" style={{ color: labelColor }}>{t.subtitle}</p>
          <form onSubmit={handleSubmit} className="cf-form">
            <div className="cf-row">
              <div className="cf-field">
                <label style={{ color: labelColor }}>
                  {t.name} <span className="cf-req">*</span>
                </label>
                <input
                  name="name"
                  required
                  value={form.name}
                  onChange={handleChange}
                  placeholder={t.namePlaceholder}
                  style={{ backgroundColor: inputBg, borderColor: inputBorder, color: theme.text }}
                />
              </div>
              <div className="cf-field">
                <label style={{ color: labelColor }}>
                  {t.email} <span className="cf-req">*</span>
                </label>
                <input
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  placeholder={t.emailPlaceholder}
                  style={{ backgroundColor: inputBg, borderColor: inputBorder, color: theme.text }}
                />
              </div>
            </div>

            <div className="cf-field">
              <label style={{ color: labelColor }}>{t.phone}</label>
              <input
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder={t.phonePlaceholder}
                style={{ backgroundColor: inputBg, borderColor: inputBorder, color: theme.text }}
              />
            </div>

            <div className="cf-field">
              <label style={{ color: labelColor }}>
                {t.service} <span className="cf-req">*</span>
              </label>
              <div
                className="cf-select-wrap"
                style={{ backgroundColor: inputBg, borderColor: inputBorder }}
              >
                <select
                  name="service"
                  required
                  value={form.service}
                  onChange={handleChange}
                  style={{ backgroundColor: inputBg, color: theme.text }}
                >
                  {services.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <span className="cf-caret" style={{ color: labelColor }}>▾</span>
              </div>
            </div>

            <div className="cf-field">
              <label style={{ color: labelColor }}>
                {t.details} <span className="cf-req">*</span>
              </label>
              <textarea
                name="details"
                required
                value={form.details}
                onChange={handleChange}
                placeholder={t.detailsPlaceholder}
                rows={5}
                style={{ backgroundColor: inputBg, borderColor: inputBorder, color: theme.text }}
              />
            </div>

            <button
              type="submit"
              className="cf-send-btn"
              style={{ backgroundColor: theme.accentBright }}
              disabled={submitting}
            >
              {submitting ? t.sending : `${t.send} →`}
            </button>

            {status === "success" && (
              <p className="cf-status cf-status--success">{t.success}</p>
            )}
            {status === "error" && (
              <p className="cf-status cf-status--error">{t.error}</p>
            )}
          </form>

          <a
            href={greeting.calendlyLink}
            target="_blank"
            rel="noopener noreferrer"
            className="cf-meeting-link"
            style={{ color: labelColor }}
          >
            <span>📅</span> {t.bookMeeting}
          </a>
        </div>

        {/* ── RIGHT: Info ─────────────────────────────── */}
        <div className="cf-info">
          <h2 className="cf-info-title" style={{ color: theme.text }}>{t.infoTitle}</h2>
          <p className="cf-info-desc" style={{ color: labelColor }}>{t.infoDesc}</p>

          <div className="cf-divider" style={{ borderColor: inputBorder }} />

          <div className="cf-info-cards">

            {/* Email */}
            <div className="cf-info-card" style={{ backgroundColor: cardBg }}>
              <ContactCardIcon type="email" />
              <div className="cf-info-card-body">
                <span className="cf-info-card-label" style={{ color: theme.secondaryText }}>EMAIL</span>
                <span className="cf-info-card-value" style={{ color: theme.text }}>{greeting.mail}</span>
              </div>
              <a href={`mailto:${greeting.mail}`} className="cf-info-card-action" style={{ color: theme.accentBright }}>
                {t.sendEmail} &rsaquo;
              </a>
            </div>

            {/* LinkedIn */}
            <div className="cf-info-card" style={{ backgroundColor: cardBg }}>
              <ContactCardIcon type="linkedin" />
              <div className="cf-info-card-body">
                <span className="cf-info-card-label" style={{ color: theme.secondaryText }}>LINKEDIN</span>
                <span className="cf-info-card-value" style={{ color: theme.text }}>{tGreeting.fullName}</span>
              </div>
              <a
                href={socialMediaLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="cf-info-card-action"
                style={{ color: theme.accentBright }}
              >
                {t.connect} &rsaquo;
              </a>
            </div>

            {/* GitHub */}
            <div className="cf-info-card" style={{ backgroundColor: cardBg }}>
              <ContactCardIcon type="github" />
              <div className="cf-info-card-body">
                <span className="cf-info-card-label" style={{ color: theme.secondaryText }}>GITHUB</span>
                <span className="cf-info-card-value" style={{ color: theme.text }}>X-iT</span>
              </div>
              <a
                href={socialMediaLinks.github}
                target="_blank"
                rel="noopener noreferrer"
                className="cf-info-card-action"
                style={{ color: theme.accentBright }}
              >
                {t.visit} &rsaquo;
              </a>
            </div>

          </div>

        </div>

      </div>
    </div>
  );
}
